import { useEffect, useId, useRef, type ReactNode } from "react";
import "./Modal.css";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Optional id of an element (usually the first field) to focus once the
   * dialog opens, overriding the browser's default "first focusable
   * element" choice. */
  initialFocusId?: string;
  /** "md" (default) fits the small forms (secrets, host enrollment); "lg" is
   * for content that needs real width, e.g. a compose-file textarea. */
  size?: "md" | "lg";
}

/**
 * A native `<dialog>`-backed modal. Using the platform element gets a lot of
 * accessibility for free — `showModal()` traps focus inside, restores focus
 * to the trigger on close, and the Escape key closes it (firing `cancel`
 * then `close`) without any keydown handling here. We only add: syncing the
 * imperative open/close to the `open` prop, closing on a backdrop click, and
 * a visible close button for pointer users who don't know Escape.
 */
export function Modal({ open, title, onClose, children, initialFocusId, size = "md" }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      if (initialFocusId) {
        const el = document.getElementById(initialFocusId);
        if (el instanceof HTMLElement) el.focus();
      }
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, initialFocusId]);

  // The dialog can also close itself (Escape, or a `<form method="dialog">`
  // submit) without us calling onClose — listen for the native `close` event
  // so the parent's `open` state stays in sync either way.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  return (
    // A click lands with target === the dialog element itself only when it
    // hits the backdrop area (any click on real content inside targets that
    // descendant instead) — the standard way to detect a backdrop click.
    // This is a pointer-only convenience on top of the dialog's native
    // Escape-to-close and the explicit close button below, not the only way
    // to dismiss it — jsx-a11y flags a bare click handler on a
    // non-interactive element regardless, but there's no keyboard
    // equivalent to add here that Escape doesn't already cover.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={`modal modal--${size}`}
      aria-labelledby={titleId}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="modal__panel">
        <div className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <button type="button" className="modal__close" aria-label="Close dialog" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </dialog>
  );
}
